import { useEffect, useState } from 'react';

import {
  CreateUpdateExamDto,
  CreateUpdateExamDtoSellDocumentTypeEnum,
  GenerateReceiptDto,
  GenerateReceiptDtoPaymentMethodEnum,
  GenerateReceiptDtoReceiptTypeEnum,
  ReceiptItemDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { ExamEntity } from '../../../common/spdCore/contexts/ReportContext';
import {
  RECEIPT_DEFAULT_ITEM,
  mapPaymentMethodToType,
} from '../../../common/spdCore/contexts/FiscalPrinterContext';
import mathUtils from '../../../utils/mathUtils';
import { PrinterError } from '../../../common/printer/fiscalPrinterErrors';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { validateAgainst } from '../../../common/spdCore/validation/validateAgainst';
import { GenerateReceiptSchema } from '../../../common/spdCore/validation/schemas';
import { usePrinterService } from '../../../common/hooks/usePrinterService';

const calculateTotal = (items: ReceiptItemDto[]): number =>
  items.reduce((sum, item) => {
    const price = mathUtils.parse(item.price);
    return mathUtils.add(sum, mathUtils.multiply(price, item.quantity));
  }, 0);

const calculateChange = (paid: string, items: ReceiptItemDto[]): number =>
  mathUtils.add(
    mathUtils.parse(paid || '0'),
    mathUtils.multiply(calculateTotal(items), -1),
  );

const parseErrorMessages = async (error: unknown): Promise<string[]> => {
  if (error instanceof PrinterError) {
    return [error.message];
  }
  if (error instanceof ResponseError) {
    return await responseErrors(error as ResponseError);
  }
  if (error instanceof Error) {
    return [error.message];
  }
  return ['Błąd podczas wysyłania paragonu'];
};

interface UseReceiptFormProps {
  exam: ExamEntity;
  onCompleted?: (exam: CreateUpdateExamDto) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

type ReceiptFormData = Omit<GenerateReceiptDto, 'printerUrl'>;

export const useReceiptForm = ({ exam, onCompleted }: UseReceiptFormProps) => {
  const spd = useSpdCore();
  const [receiptData, setReceiptData] = useState<ReceiptFormData>({
    examId: exam.id,
    paymentMethod: GenerateReceiptDtoPaymentMethodEnum.Cash,
    items: [RECEIPT_DEFAULT_ITEM],
    receiptType: GenerateReceiptDtoReceiptTypeEnum.Fiscal,
  });
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [jpkid, setJpkid] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const printerService = usePrinterService();

  useEffect(() => {
    return printerService.onDeviceStatus(event => {
      if (event.status === 'ERROR') {
        setShowSaveErrors(true);
        setSaveErrors([event.message]);
      } else {
        setShowSaveErrors(false);
        setSaveErrors([]);
      }
    });
  }, [printerService]);

  const handlePaymentMethodChange = (
    paymentMethod: GenerateReceiptDtoPaymentMethodEnum,
  ) => {
    const paid = mathUtils.toFixed(calculateTotal(receiptData.items));
    setReceiptData({ ...receiptData, paymentMethod, paid });
  };

  const handleItemChange = (
    index: number,
    field: keyof ReceiptItemDto,
    value: string | number,
  ) => {
    const newItems = receiptData.items.map((item, i) =>
      i !== index
        ? item
        : { ...item, [field]: field === 'quantity' ? Number(value) : value },
    );
    const paid = mathUtils.toFixed(calculateTotal(newItems));
    setReceiptData({ ...receiptData, items: newItems, paid });
  };

  const addReceiptItem = () => {
    const newItems = [...receiptData.items, RECEIPT_DEFAULT_ITEM];
    const paid = mathUtils.toFixed(calculateTotal(newItems));
    setReceiptData({ ...receiptData, items: newItems, paid });
  };

  const removeReceiptItem = (index: number) => {
    if (receiptData.items.length > 1) {
      setReceiptData({
        ...receiptData,
        items: receiptData.items.filter((_, i) => i !== index),
      });
    }
  };

  const performSave = async (
    receiptType?: GenerateReceiptDtoReceiptTypeEnum,
  ) => {
    const validationResult = await validateAgainst(
      GenerateReceiptSchema,
      receiptData,
    );
    setShowSuccess(false);
    if (!validationResult.valid) {
      setSaveErrors(validationResult.errors);
      setShowSaveErrors(true);
      return;
    }
    setIsLoading(true);
    try {
      const data = receiptType ? { ...receiptData, receiptType } : receiptData;
      const {
        jpkid: newJpkid,
        receiptId,
        printerUid,
        info,
      } = await spd.fiscalPrinter.generateAndPrintReceipt(data);
      setJpkid(newJpkid);
      if (
        !receiptType ||
        receiptType === GenerateReceiptDtoReceiptTypeEnum.Fiscal
      ) {
        await spd.receipts.fiscalize(receiptId, newJpkid, printerUid);
      }
      if (printerUid && newJpkid) {
        try {
          await spd.printerJpkDocuments.sync(printerUid, {
            upToJpkId: newJpkid,
            maxDocs: 5,
            stopOnError: true,
            includeMissing: false,
          });
        } catch (syncError) {
          console.error('[JPK sync] Sync failed:', syncError);
        }
      }
      if (info) {
        setSaveErrors([info]);
        setShowSaveErrors(true);
      } else {
        const message =
          receiptType === GenerateReceiptDtoReceiptTypeEnum.EFiscal
            ? 'E-paragon został pomyślnie wysłany.'
            : 'Paragon został pomyślnie wysłany i wydrukowany.';
        setShowSuccess(true);
        setSuccessMessage(message);
        setShowSaveErrors(false);
        setSaveErrors([]);
      }
      if (onCompleted) {
        await onCompleted({
          ...(exam as CreateUpdateExamDto),
          paid: mathUtils.toFixed(
            mathUtils.add(
              mathUtils.parse(exam.paid || '0'),
              calculateTotal(receiptData.items),
            ),
          ),
          paymentType: mapPaymentMethodToType(receiptData.paymentMethod),
          sellDocumentType: CreateUpdateExamDtoSellDocumentTypeEnum.Paragon,
          paymentDate: new Date(),
        });
      }
    } catch (error) {
      setSaveErrors(await parseErrorMessages(error));
      setShowSaveErrors(true);
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => performSave();
  const handleSaveEFiscal = () =>
    performSave(GenerateReceiptDtoReceiptTypeEnum.EFiscal);

  const total = calculateTotal(receiptData.items);
  const isButtonDisabled =
    isLoading || calculateChange(receiptData.paid || '', receiptData.items) < 0;

  return {
    receiptData,
    setReceiptData,
    showSaveErrors,
    saveErrors,
    showSuccess,
    successMessage,
    jpkid,
    isLoading,
    total,
    isButtonDisabled,
    handlePaymentMethodChange,
    handleItemChange,
    addReceiptItem,
    removeReceiptItem,
    handleSave,
    handleSaveEFiscal,
  };
};
